export interface SimpleResponse extends Response {
    status: number;
    message: string;
}

/**
 * Error returned by the JSON endpoints that need to distinguish validation,
 * authorization and missing-resource responses.
 */
export interface RequestError extends Error {
    status: number;
}

class Request {
    static OnLogout: Function|null = null;
    static ContentType: string = 'application/json';
    // static BearerToken: string = '';

    static DoPost = (endpoint: string, data: object, contentType: string = '', cache: boolean = false) => {
        let headers: any = {
            // ...(Request.BearerToken
            //         ? { 'authorization': 'Bearer ' + Request.BearerToken }
            //         : {})
        };

        if (contentType !== 'multipart/form-data') {
            headers['Accept'] = contentType !== '' ? contentType : Request.ContentType;
            headers['Content-Type'] = contentType !== '' ? contentType : Request.ContentType;
        }

        const url = process.env.REACT_APP_DOMAIN + '' +
                    process.env.REACT_APP_API + 
                    endpoint;

        return fetch(url, {
            method: 'POST',
            cache:'no-cache',
            headers: headers,
            credentials: 'include',
            body: contentType === '' ? JSON.stringify(data) : data as FormData,
        }).then(response => {
            if (response.status === 401) {
                Request.OnLogout && Request.OnLogout();
                return Promise.reject(new Error('Unauthorized'));
            }
            return response.json();
        }).catch(function (error) {
            throw Error(error);
        });
    };

    static DoGet = (endpoint: string, cache: boolean = false) => {
        const url = process.env.REACT_APP_DOMAIN + '' +
                    process.env.REACT_APP_API + 
                    endpoint;

        return fetch(url, {
            method: 'GET',
            cache: 'no-cache',
            credentials: 'include',
            headers: {
                'Content-Type': Request.ContentType,
                'Accept': Request.ContentType,
                // ...(Request.BearerToken
                //     ? { 'authorization': 'Bearer ' + Request.BearerToken }
                //     : {})
            }
        }).then(response => {
            if (response.status === 401) {
                Request.OnLogout && Request.OnLogout();
                return Promise.reject(new Error('Unauthorized'));
            }
            return response.json();
        }).catch(function (error) {
            throw Error(error);
        });
    };

    /**
     * Structured JSON request used by new APIs. The older DoGet/DoPost/etc.
     * deliberately retain their historic response handling, since several
     * existing pages consume their status/message payloads directly.
     */
    static DoJson = async <T>(endpoint: string, method: string = 'GET', data?: object): Promise<T> => {
        const url = process.env.REACT_APP_DOMAIN + '' +
                    process.env.REACT_APP_API + 
                    endpoint;

        const headers: any = {
            'Accept': Request.ContentType,
            // ...(Request.BearerToken
            //     ? { 'authorization': 'Bearer ' + Request.BearerToken }
            //     : {})
        };

        if (data !== undefined) {
            headers['Content-Type'] = Request.ContentType;
        }

        const response = await fetch(url, {
            method,
            cache: 'no-cache',
            credentials: 'include',
            headers,
            ...(data !== undefined ? { body: JSON.stringify(data) } : {})
        });

        if (response.status === 401) {
            Request.OnLogout && Request.OnLogout();
        }

        const text = await response.text();
        let body: any = null;

        if (text) {
            try {
                body = JSON.parse(text);
            } catch (_) {
                body = null;
            }
        }

        if (!response.ok) {
            const error = new Error(body?.message ?? body?.Message ?? text ?? `Request failed (${response.status})`) as RequestError;
            error.status = response.status;
            throw error;
        }

        return body as T;
    };

    static DoPatch = <T>(endpoint: string, data?: object): Promise<T> => {
        return Request.DoJson<T>(endpoint, 'PATCH', data);
    };

    static DoDownload = async (
        endpoint: string,
        fileName: string,
        accept: string = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ): Promise<void> => {
        const url = process.env.REACT_APP_DOMAIN + '' +
                    process.env.REACT_APP_API + 
                    endpoint;

        const response = await fetch(url, {
            method: 'GET',
            cache: 'no-cache',
            credentials: 'include',
            headers: {
                'Accept': accept,
                // ...(Request.BearerToken
                //     ? { 'authorization': 'Bearer ' + Request.BearerToken }
                //     : {})
            }
        });

        if (response.status === 401) {
            Request.OnLogout && Request.OnLogout();
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            let message = `Download failed (${response.status})`;

            try {
                const errorResponse = await response.json();
                message = errorResponse?.message ?? errorResponse?.Message ?? message;
            } catch (_) {
                // Keep the status-based fallback when the API does not return JSON.
            }

            throw new Error(message);
        }

        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        try {
            link.href = objectUrl;
            link.download = fileName;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
        } finally {
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
            window.URL.revokeObjectURL(objectUrl);
        }
    };

    static DoPut = (endpoint: string, data: object, contentType: string = '', cache: boolean = false) => {
        let headers: any = {
            // ...(Request.BearerToken
            //         ? { 'authorization': 'Bearer ' + Request.BearerToken }
            //         : {})
        };

        if (contentType !== 'multipart/form-data') {
            headers['Accept'] = contentType !== '' ? contentType : Request.ContentType;
            headers['Content-Type'] = contentType !== '' ? contentType : Request.ContentType; 
        }
        headers['Accept'] = contentType !== '' ? contentType : Request.ContentType;
               
        const url = process.env.REACT_APP_DOMAIN + '' +
                    process.env.REACT_APP_API + 
                    endpoint;

        return fetch(url, {
            method: 'PUT',
            cache: 'no-cache',
            credentials: 'include',
            headers: headers,
            body: contentType === '' ? JSON.stringify(data) : data as FormData,
        }).then(response => {
            if (response.status === 401) {
                Request.OnLogout && Request.OnLogout();
                return Promise.reject(new Error('Unauthorized'));
            }
            return response.json();
        }).catch(function (error) {
            throw Error(error);
        });
    };

    static DoDelete = (endpoint: string) => {
        const url = process.env.REACT_APP_DOMAIN + '' +
                    process.env.REACT_APP_API + 
                    endpoint;

        return fetch(url, {
            method: 'DELETE',
            credentials: 'include',
            headers: {
                'Content-Type': Request.ContentType,
                'Accept': Request.ContentType,
                // ...(Request.BearerToken
                //     ? { 'authorization': 'Bearer ' + Request.BearerToken }
                //     : {})
            },
        }).then(response => {
            if (response.status === 401) {
                Request.OnLogout && Request.OnLogout();
                return Promise.reject(new Error('Unauthorized'));
            }
            return response.json();
        }).catch(function (error) {
            throw Error(error);
        });
    };

    /*
     
    body: JSON.stringify(data), // must match 'Content-Type' header
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    credentials: 'same-origin', // include, same-origin, *omit
    headers: {
        'user-agent': 'Mozilla/4.0 MDN Example',
        'content-type': 'application/json'
    },
    method: 'POST', // *GET, POST, PUT, DELETE, etc.
    mode: 'cors', // no-cors, cors, *same-origin
    redirect: 'follow', // manual, *follow, error
    referrer: 'no-referrer', // *client, no-referrer
    
    */
}
export default Request;
